import { z } from 'zod';
import { createModel, partySchema, QueryMany, QueryOne, statusCommonSchema } from '../../common';
import { businessSchema } from '../../common/app-registry.schema';
import { emailSchema, nameSchema, phoneSchema } from '../../validation';
import { languageSchema } from '../../common/language.schema';
import { referralRewardRuleSchema } from './referral.reward-rule.model';

/**
 * Who a partner is to us.
 *
 * - `marketer`  — an outside affiliate / influencer / agency paid in cash.
 * - `customer`  — an existing account (Org / User / Driver) referring
 *                 peers, usually paid in platform credits.
 * - `employee`  — Hero staff or a sales hunter whose link tracks their
 *                 on-ground work; typically `reward.trigger` is `none`.
 * - `other`     — anything else.
 *
 * Reporting bucket only; nothing in the evaluator branches on it. What
 * a partner earns is `reward`, whether they can receive credits is
 * whether `owner` is set.
 */
export const referralPartnerTypeSchema = z.enum(['marketer', 'customer', 'employee', 'other']);
export type ReferralPartnerType = z.infer<typeof referralPartnerTypeSchema>;

/**
 * `ReferralPartner` collection — one row per person or company that
 * refers business to the platform. Admin-managed.
 *
 * A partner owns any number of `ReferralLink`s, each with its own code,
 * title, product target and optional reward override. Tracking hits,
 * attributed leads and rewards all point at a link; the partner is the
 * grouping the finance team pays.
 *
 * Expected indexes (created by the `referral-collections` migration):
 * - `{ 'owner._id': 1 }` sparse — "is this account already a partner".
 * - `{ 'doc.status': 1 }` — active-partner listing.
 * - Atlas Search over `name`, `contact.phone`, `contact.email`.
 */
export const ReferralPartnerSchema = createModel({
  /** Display name — the person or company. */
  name: nameSchema,
  /** Reporting bucket. See {@link referralPartnerTypeSchema}. */
  type: referralPartnerTypeSchema.default('marketer'),
  /**
   * What this partner is allowed to market — Hero AI, Wathiqa Plus, or
   * both. Bounds the links they can own (`ReferralLink.business` must be
   * one of these), the businesses the reward editor prices, and the
   * Billings that earn a reward: a purchase under a business not listed
   * here is tracked but never paid out. ≥ 1.
   */
  businesses: z
    .array(businessSchema)
    .min(1)
    .default(() => [...businessSchema.options]),
  /**
   * The platform account behind this partner, when there is one — the
   * Org / User / Driver an existing customer refers from. Required for
   * `order_credits` rewards (credits land on this account's usage
   * subscription); absent for outside marketers paid in cash.
   */
  owner: partySchema.optional(),
  /** How the finance team reaches the partner about payouts. */
  contact: z
    .object({
      phone: phoneSchema.optional(),
      email: emailSchema.optional(),
      /** Preferred language for payout notices. */
      language: languageSchema.optional(),
    })
    .default({}),
  /**
   * The standing deal. Every link inherits this unless it carries its
   * own `reward`. Empty `parts` = tracking only.
   */
  reward: referralRewardRuleSchema.default({}),
  /**
   * `active` partners' links resolve; `blocked` / `inactive` partners
   * keep their history but new hits and leads stop attributing to them.
   */
  status: statusCommonSchema,
  /** Free-form admin notes (payout details, contract reference, …). */
  notes: z.string().trim().optional(),
});

export type ReferralPartner = z.infer<typeof ReferralPartnerSchema.model>;
export type ReferralPartnerData = z.infer<typeof ReferralPartnerSchema.data>;
export type ReferralPartnerPartial = z.infer<typeof ReferralPartnerSchema.partial>;
export type ReferralPartnerQueryMany = QueryMany<ReferralPartner>;
export type ReferralPartnerQueryOne = QueryOne<ReferralPartner>;
